import type {
  DocsBridge,
  DocsBridgeEventHandler,
  DocsBridgeRequest,
  DocsBridgeResponse,
  DocsHostContext,
} from '@/bridge/types';
import { docsApi, DocsApiError } from './api';

interface StandaloneBridgeActions {
  navigateDocument(
    documentId: string,
    targetBlockId?: string,
    revisionId?: string,
  ): void;
  navigateHome(): void;
  goBack(): void;
}

function failure(error: unknown): DocsBridgeResponse {
  return {
    ok: false,
    error: {
      code:
        error instanceof DocsApiError && error.code !== undefined
          ? String(error.code)
          : 'E_DOCS_REQUEST',
      message: error instanceof Error ? error.message : '文档服务请求失败',
    },
  };
}

export function createDirectDocsBridge(
  context: DocsHostContext,
  onEvent: (event: string, payload?: unknown) => void,
): DocsBridge {
  const listeners = new Map<string, Set<DocsBridgeEventHandler>>();

  return {
    async request<T = unknown>(
      request: DocsBridgeRequest,
    ): Promise<DocsBridgeResponse<T>> {
      const params = request.params || {};
      try {
        let data: unknown;
        switch (request.method) {
          case 'context.get':
            data = { ...context, locale: navigator.language };
            break;
          case 'auth.get': {
            const session = await docsApi.getSession();
            data = {
              authorized: session.authenticated,
              admin: session.admin,
            };
            break;
          }
          case 'document.list':
            data = await docsApi.listDocuments();
            break;
          case 'document.revisions.list':
            data = await docsApi.listRevisions(
              String(params.documentId || context.documentId),
            );
            break;
          case 'document.revision.delete':
            data = await docsApi.deleteRevision(
              String(params.documentId || context.documentId),
              String(params.revisionId || ''),
            );
            break;
          case 'document.get':
            data = await docsApi.getDocument(
              String(params.documentId || context.documentId),
              typeof params.revisionId === 'string'
                ? params.revisionId
                : context.revisionId,
            );
            break;
          case 'document.search':
            data = await docsApi.searchDocuments({
              query: String(params.query || ''),
              documentId:
                typeof params.documentId === 'string'
                  ? params.documentId
                  : undefined,
              limit:
                typeof params.limit === 'number' ? params.limit : undefined,
            });
            break;
          case 'document.saveDraft':
            data = await docsApi.saveDraft(
              String(params.documentId || context.documentId),
              String(params.baseRevisionId || ''),
              String(params.latestRevisionId || params.baseRevisionId || ''),
              Array.isArray(params.blocks) ? params.blocks : [],
            );
            break;
          case 'document.publish':
            data = await docsApi.publish(
              String(params.documentId || context.documentId),
              String(params.revisionId || ''),
            );
            break;
          case 'document.unpublish':
            data = await docsApi.unpublish(
              String(params.documentId || context.documentId),
              String(params.revisionId || ''),
            );
            break;
          case 'document.asset.upload':
          case 'document.image.upload': {
            const file = params.file;
            if (!(file instanceof File)) {
              throw new DocsApiError('请选择需要上传的文件');
            }
            data = await docsApi.uploadAsset(
              String(params.documentId || context.documentId),
              file,
              request.method === 'document.image.upload',
            );
            break;
          }
          default:
            throw new DocsApiError(`不支持的方法：${request.method}`);
        }
        return { ok: true, data } as DocsBridgeResponse<T>;
      } catch (error) {
        return failure(error) as DocsBridgeResponse<T>;
      }
    },

    emit(event, payload) {
      onEvent(event, payload);
      listeners.get(event)?.forEach(handler => handler(payload));
    },

    subscribe(event, handler) {
      const handlers = listeners.get(event) || new Set();
      handlers.add(handler);
      listeners.set(event, handlers);
      return () => {
        const current = listeners.get(event);
        current?.delete(handler);
        if (current?.size === 0) listeners.delete(event);
      };
    },
  };
}

export function createStandaloneBridge(
  context: DocsHostContext,
  actions: StandaloneBridgeActions,
) {
  return createDirectDocsBridge(context, (event, payload) => {
    const value =
      payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>)
        : {};
    if (
      (event === 'docs.openDocument' || event === 'docs.openLinkedDocument') &&
      typeof value.documentId === 'string'
    ) {
      actions.navigateDocument(
        value.documentId,
        typeof value.targetBlockId === 'string'
          ? value.targetBlockId
          : undefined,
        typeof value.revisionId === 'string' ? value.revisionId : undefined,
      );
    } else if (event === 'docs.home') {
      actions.navigateHome();
    } else if (event === 'docs.back' || event === 'docs.close') {
      actions.goBack();
    } else if (event === 'docs.openFull') {
      actions.navigateDocument(
        context.documentId,
        context.targetBlockId,
        context.revisionId,
      );
    }
  });
}
